import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { emitWidgetReady } from 'services/eventBus/narrativeWidgetsEventBus';

import useUserHook from 'hooks/useUserHook';

import { useMutationGetReportExecSummary } from 'api/mutations/getReportExecSummary.mutation';

import {
    MAX_WAIT_MS,
    ParsedMessage,
} from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/hooks/useNarrative';

const REPORT_SUMMARY_MESSAGE_TYPE = 'SummaryReportGenerated';
const REPORT_SUMMARY_TIMEOUT_MESSAGE = 'No results match the applied filter.';

interface ReportPageUrlParams {
    reportId: string;
}

interface UseReportSummaryProviderProps {
    widgetId?: number;
}

export const useReportSummaryProvider = ({ widgetId }: UseReportSummaryProviderProps = {}) => {
    const [reportSummary, setReportSummary] = useState<string | undefined>(undefined);

    const { email } = useUserHook();
    const reportId = Number(useParams<ReportPageUrlParams>().reportId);

    const { mutate: triggerReportExecSummary } = useMutationGetReportExecSummary();

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body) as ParsedMessage;
        if (type === REPORT_SUMMARY_MESSAGE_TYPE) {
            const { key: reportIdApiResponse, value: generatedReportSummaryApiResponse } = message;
            const matchesCurrentReport = Number(reportIdApiResponse) === reportId;
            if (matchesCurrentReport) {
                setReportSummary(generatedReportSummaryApiResponse);
                //Notify that the widget is ready
                //used for tracking loading state in reports for enable export when all narrative widgets are ready
                if (widgetId !== undefined) {
                    emitWidgetReady(widgetId);
                }
            }
        }
    });

    useEffect(() => {
        triggerReportExecSummary({ id: reportId });

        // Set timeout to display message if no results received
        const timeoutId = setTimeout(() => {
            setReportSummary((currentSummary) => {
                // Only set timeout message if no summary has been received yet
                if (!currentSummary) {
                    return REPORT_SUMMARY_TIMEOUT_MESSAGE;
                }
                return currentSummary;
            });
            //Notify that the widget is ready - to enable export even if no data
            if (widgetId !== undefined) {
                emitWidgetReady(widgetId);
            }
        }, MAX_WAIT_MS);

        // Cleanup timeout on unmount
        return () => clearTimeout(timeoutId);
    }, [triggerReportExecSummary]);

    return {
        ui: {
            reportSummary,
        },
    };
};
